import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { waLink, SOCIAL } from "@/lib/content";

const IG_PATH =
  "M12 2.2c3.2 0 3.6 0 4.9.1 1.2.1 1.8.3 2.2.4.6.2 1 .5 1.4.9.4.4.7.8.9 1.4.2.4.4 1 .4 2.2.1 1.3.1 1.7.1 4.9s0 3.6-.1 4.9c-.1 1.2-.3 1.8-.4 2.2-.2.6-.5 1-.9 1.4-.4.4-.8.7-1.4.9-.4.2-1 .4-2.2.4-1.3.1-1.7.1-4.9.1s-3.6 0-4.9-.1c-1.2-.1-1.8-.3-2.2-.4-.6-.2-1-.5-1.4-.9-.4-.4-.7-.8-.9-1.4-.2-.4-.4-1-.4-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.1-4.9c.1-1.2.3-1.8.4-2.2.2-.6.5-1 .9-1.4.4-.4.8-.7 1.4-.9.4-.2 1-.4 2.2-.4C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.1-1.1.1-1.7.2-2.1.4-.5.2-.9.4-1.3.8-.4.4-.6.8-.8 1.3-.2.4-.3 1-.4 2.1-.1 1.2-.1 1.6-.1 4.7s0 3.5.1 4.7c.1 1.1.2 1.7.4 2.1.2.5.4.9.8 1.3.4.4.8.6 1.3.8.4.2 1 .3 2.1.4 1.2.1 1.6.1 4.7.1s3.5 0 4.7-.1c1.1-.1 1.7-.2 2.1-.4.5-.2.9-.4 1.3-.8.4-.4.6-.8.8-1.3.2-.4.3-1 .4-2.1.1-1.2.1-1.6.1-4.7s0-3.5-.1-4.7c-.1-1.1-.2-1.7-.4-2.1-.2-.5-.4-.9-.8-1.3-.4-.4-.8-.6-1.3-.8-.4-.2-1-.3-2.1-.4-1.2-.1-1.6-.1-4.7-.1zm0 3.1a4.9 4.9 0 110 9.8 4.9 4.9 0 010-9.8zm0 8.1a3.2 3.2 0 100-6.4 3.2 3.2 0 000 6.4zm6.2-8.3a1.15 1.15 0 11-2.3 0 1.15 1.15 0 012.3 0z";
const FB_PATH =
  "M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5 3.66 9.15 8.44 9.94v-7.03H7.9v-2.9h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.46h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.9h-2.34V22c4.78-.79 8.44-4.94 8.44-9.94z";
const WA_PATH =
  "M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884M20.52 3.449C18.24 1.245 15.24 0 12.05 0 5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413";

const goldBtn =
  "flex h-12 w-12 items-center justify-center rounded-full border border-gold bg-navy/70 text-goldsoft backdrop-blur-md shadow-[0_8px_24px_-8px_rgba(0,0,0,0.6)] transition-colors duration-300 hover:bg-gold hover:text-navy";

const item = {
  hidden: { opacity: 0, y: 12, scale: 0.8 },
  show: { opacity: 1, y: 0, scale: 1 },
};

const canHover = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

export const WhatsAppButton = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const hoverDevice = canHover();

  return (
    <div
      className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3"
      onMouseEnter={hoverDevice ? () => setOpen(true) : undefined}
      onMouseLeave={hoverDevice ? () => setOpen(false) : undefined}
      data-testid="social-dock"
    >
      <AnimatePresence>
        {open && (
          <>
            <motion.a
              key="ig"
              href={SOCIAL.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="floating-instagram"
              aria-label="Instagram"
              variants={item}
              initial="hidden"
              animate="show"
              exit="hidden"
              transition={{ duration: 0.3, delay: 0.06, ease: [0.16, 1, 0.3, 1] }}
              className={goldBtn}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                <path d={IG_PATH} />
              </svg>
            </motion.a>
            <motion.a
              key="fb"
              href={SOCIAL.facebook}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="floating-facebook"
              aria-label="Facebook"
              variants={item}
              initial="hidden"
              animate="show"
              exit="hidden"
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className={goldBtn}
            >
              <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden="true">
                <path d={FB_PATH} />
              </svg>
            </motion.a>
          </>
        )}
      </AnimatePresence>

      <button
        type="button"
        onClick={() => (hoverDevice ? setOpen(true) : setOpen((o) => !o))}
        data-testid="social-toggle"
        aria-label="Social"
        aria-expanded={open}
        className={goldBtn}
      >
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-2xl leading-none"
        >
          +
        </motion.span>
      </button>

      <motion.a
        href={waLink(t.waMsg)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp-button"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.96 }}
        className="group flex items-center gap-3 rounded-full bg-navy py-3 pl-3 pr-3 md:pr-5 text-canvas shadow-[0_10px_40px_-8px_rgba(10,25,47,0.6)] ring-1 ring-gold/40"
        aria-label="WhatsApp"
      >
        <span className="flex h-11 w-11 items-center justify-center rounded-full bg-canvas text-navy">
          <svg viewBox="0 0 24 24" className="h-6 w-6 fill-current" aria-hidden="true">
            <path d={WA_PATH} />
          </svg>
        </span>
        <span className="hidden md:block pr-1 font-medium text-sm tracking-wide">{t.hero.cta}</span>
      </motion.a>
    </div>
  );
};
